// Runs the viewer-core pipeline against an in-memory VFS of .arch
// sources and exposes the wire-format model/edges/diagnostics that
// the rest of the app already consumes.
//
// No server, no SSE — when the sources Map changes, we re-run the
// pipeline. Pass a new Map to trigger reload (cheap; the pipeline
// is pure and fast for small workspaces).

import { useEffect, useRef, useState } from "react";
import {
  callWireEdges,
  createIncrementalSerializer,
  generateWidgetsCss,
  IncrementalViewer,
  serializeDiagnostics,
  type Vfs,
  type WireArchitectureModel,
  type WireMarkdownDocument,
  type WireBacklink,
} from "@archlang/viewer-core";
import {
  computeBlastRadius,
  type DependencyGraph,
  type StableId as CoreStableId,
  type ProcessModel,
  type SubprocessModel,
  type ResolvedModel,
} from "@archlang/engine";
import type {
  ArchitectureModel,
  DependencyEdge,
  Diagnostic,
  StableId,
} from "@archlang/scene/arch-model";
import { setWidgetsCss } from "../board/builtinWidgets";
import { stampQualifiedKeys } from "@archlang/scene/arch-model";

export interface ModelState {
  model: ArchitectureModel | null;
  diagnostics: Diagnostic[];
  edges: DependencyEdge[];
  /** Markdown documents (§4.15) — rendered flavored-markdown + section
   *  anchors. Empty when the workspace declares no `.md` documents. These
   *  live only on the wire model, not the scene `ArchitectureModel`, so they
   *  are surfaced as their own slice rather than read off `model`. */
  documents: readonly WireMarkdownDocument[];
  /** "Mentioned in" backlinks keyed by a node's short name, qualified name,
   *  `#id`, or a document name. Empty when nothing references anything. */
  backlinks: Readonly<Record<string, readonly WireBacklink[]>>;
  /** Raw dependency graph kept around so callers can answer graph
   *  questions (blast radius, dependents, dependencies) without
   *  re-running the pipeline. Null until the first load resolves. */
  graph: DependencyGraph | null;
  /** Whether the last compile changed the symbol/import surface (add/remove/
   *  rename/re-parent/interface change) rather than only field values. The board
   *  uses it to decide how aggressively to invalidate its per-space layout cache:
   *  a pure value edit (`structural: false`) needs no cross-level invalidation. */
  structural: boolean;
  /** Engine process IR for the current compile — the process walker/orchestrator
   *  consume these (the wire `model` carries AST steps for the board; the IR is
   *  session-scoped, rebuilt every compile, never serialized to share links).
   *  Both the cold (`resolveFile`) and incremental paths attach them via the
   *  shared `assembleModel`. */
  processModels: readonly ProcessModel[];
  subprocessModels: readonly SubprocessModel[];
  /** The resolved engine model — the clause-carrying `ViewDeclV2` views +
   *  `presence` the view projection (S15) reads. The wire `model` above reduces
   *  views to `{id,name,description}` (F.2), so this is the projection's source;
   *  it stays in-browser (never serialized). Null until the first load resolves. */
  resolvedModel: ResolvedModel | null;
  /** The EXACT sources the current `resolvedModel`/spans were compiled from.
   *  The mutation layer's generation guard compares these against the live
   *  store text before trusting any span-anchored edit (WYSIWYG plan §2) —
   *  spans are only valid against the text their compile saw. Null until the
   *  first load resolves. */
  compiledSources: Vfs | null;
  loading: boolean;
  error: string | null;
}

const EMPTY_STATE: ModelState = {
  model: null,
  diagnostics: [],
  edges: [],
  documents: [],
  backlinks: {},
  graph: null,
  structural: true,
  processModels: [],
  subprocessModels: [],
  resolvedModel: null,
  compiledSources: null,
  loading: true,
  error: null,
};

export interface ModelApi extends ModelState {
  refresh: () => Promise<void>;
  /** Cold rebuild: discard all incremental engine state and recompile from
   *  scratch. Equivalent to constructing a fresh viewer instance. */
  coldRefresh: () => Promise<void>;
  blastRadiusIds: (id: StableId) => readonly StableId[];
}

export function useModel(sources: Vfs | null, rootPath?: string): ModelApi {
  const [state, setState] = useState<ModelState>(EMPTY_STATE);
  const sourcesRef = useRef<Vfs | null>(sources);
  sourcesRef.current = sources;
  const rootPathRef = useRef<string | undefined>(rootPath);
  rootPathRef.current = rootPath;
  // Persistent incremental compiler: a content edit re-resolves only the
  // changed declarations instead of recompiling the whole workspace.
  const viewerRef = useRef<IncrementalViewer | null>(null);
  // Persistent incremental serializer: re-serializes only the changed modules
  // (cached by identity) + reuses wire edges across value edits.
  const serializerRef = useRef<ReturnType<typeof createIncrementalSerializer> | null>(null);

  const run = async (vfs: Vfs | null): Promise<void> => {
    if (!vfs) {
      setState({ ...EMPTY_STATE, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      if (!viewerRef.current) viewerRef.current = new IncrementalViewer();
      if (!serializerRef.current) serializerRef.current = createIncrementalSerializer();
      const result = await viewerRef.current.update({ sources: vfs, rootPath: rootPathRef.current });
      const wireModel: WireArchitectureModel = serializerRef.current.serializeModel(result.model, result.structural);
      // Stamp each Module with its qualified name so `moduleKey()` can
      // produce a key consistent with `edgeFromKey()` for nested
      // modules that don't have a `#id`. Without this, the board's
      // edge filter drops every cross-reference inside a nested space.
      stampQualifiedKeys(
        (wireModel as unknown as ArchitectureModel).modules,
      );
      // Compile inline widget templates' utility classes and push
      // them into the shared stylesheet adopted by every template
      // shadow root. Fire-and-forget; the model paints before the
      // sheet resolves, then re-styles when it lands.
      void generateWidgetsCss(result.model).then(setWidgetsCss).catch((err) => {
        console.warn("[arch] widget CSS compile failed", err);
      });
      setState({
        model: wireModel as unknown as ArchitectureModel,
        diagnostics: [...serializeDiagnostics(result.diagnostics)] as Diagnostic[],
        // The board renders the module→interface universe; `do` edges (and any
        // future kind) ride the same wire but are filtered here (design P0.1).
        edges: callWireEdges(serializerRef.current.serializeEdges(result.graph)) as DependencyEdge[],
        documents: wireModel.documents ?? [],
        backlinks: wireModel.backlinks ?? {},
        graph: result.graph,
        structural: result.structural,
        processModels: result.model.processModels ?? [],
        subprocessModels: result.model.subprocessModels ?? [],
        resolvedModel: result.model,
        compiledSources: vfs,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : "Unknown error",
      }));
    }
  };

  const refresh = async (): Promise<void> => {
    await run(sourcesRef.current);
  };

  const coldRefresh = async (): Promise<void> => {
    // Drop the IncrementalViewer so the next run() treats it as the very first
    // call and performs a full reseed (needsFullLoad() → true), discarding all
    // cached engine state. Also reset the serializer's identity cache.
    viewerRef.current = null;
    serializerRef.current = null;
    await run(sourcesRef.current);
  };

  // Blast radius is a graph query; we keep it on the hook so callers
  // don't import @archlang/engine. Resolves id → module → computeBlastRadius.
  // Empty when graph not loaded or id not known.
  const blastRadiusIds = (id: StableId): readonly StableId[] => {
    const graph = state.graph;
    if (!graph) return [];
    // Web's StableId is a plain string alias; core's is a branded
    // string. The cast crosses that boundary; values are identical.
    const mod = graph.modulesById.get(id as unknown as CoreStableId);
    if (!mod) return [];
    const out: StableId[] = [];
    for (const m of computeBlastRadius(graph, mod)) {
      if (m.id) out.push(m.id as unknown as StableId);
    }
    return out;
  };

  useEffect(() => {
    void run(sources);
  }, [sources, rootPath]);

  return { ...state, refresh, coldRefresh, blastRadiusIds };
}
